/**
 * @module experience/lineselectormodal
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
/*tslint:disable:max-file-line-count */
import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
// services
import { LoggerService } from '@core/logger/services/logger.service';
import { ChangePlansService } from '@experience/change-plan-services/services/change-plans.service';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { LineSelectorService } from '@experience/landing/common/components/line-selector/services/line-selector.service';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { UserService } from '@support/shared/services/user.service';
import { CommonUtilService } from '@support/shared/services/common-util.service';
// Interfaces
import { ConfigurableLineSelectorAEMData } from '@experience/landing/common/components/line-selector/interfaces/line-selector.interface';
import {
  CustomerAccountBillingDetails,
  LineSummary,
} from '@support/api/proxies/responses/api-2006.interface';
// Constants
import {
  COMMONLANDING_MODULE_COMPONENTS,
  LINE_LANDING_CONSTANTS,
} from '@experience/landing/common/components/line-landing/line-landing.constants';
import {
  CPS_ROUTES_CONSTANTS,
  CPS_CONSTANTS,
} from '@experience/change-plan-services/constants/change-plan-services.constants';
import { APP_ROUTE_CONSTANTS } from '@root/shared/constants/app-route.constants';
import { CPS_ROUTES } from '@support/shared/constants/app-routes.constants';

// Modal
import { LineSelectionModel } from '@experience/landing/common/models/line-selection.model';
import { StaticModal } from '@support/shared/widgets/interfaces/static-modal.interface';
import { SetFocusService } from '@support/shared/widgets/services/set-focus.service';
import { CartSupportService } from '@support/cart-support/services/cart-support.service';
import { OrderSearchResults } from '@support/api/proxies/responses/api-2813.interface';
import { LinePendingOrdersModel } from '@experience/landing/common/models/line-pending-orders.model';

// Pipes
import { PhonePipe } from '@support/shared/widgets/pipes/phone/phone.pipe';
import { APIError } from '@support/api/common/error.interface';

@Component({
  selector: 'tmo-line-selector',
  templateUrl: './line-selector.component.html',
  styleUrls: ['../../../../../../style-sheets/components/page-titles.component.scss'],
})
export class LineSelectorComponent implements OnInit, OnDestroy {
  constructor(
    private stateStoreUtilsService: StateStoreUtilsService,
    private setFocusService: SetFocusService,
    private cartSupportService: CartSupportService,
    private logger: LoggerService,
    private router: Router,
    private objectService: ObjectService,
    private lineSelectorService: LineSelectorService,
    private userService: UserService,
    private commonUtilService: CommonUtilService,
    private phonePipe: PhonePipe,
    private changePlanService: ChangePlansService,
    public routeSnapshot: ActivatedRoute
  ) {
    this.ngUnsubscribe = new Subject<void>();
    this.dataLoaded = false;
    this.hideSpinner = true;
  }
  /**
   * To store edited Line details
   */
  public editedLines: LineSummary[];
  /**
   * selectedOption to show selected radio button
   */
  public selectedOption: string;
  /**
   * To store filtered Line details
   */
  public nonEditedLines: LineSummary[];
  /**
   * To store aem configured data
   */
  public aemData: ConfigurableLineSelectorAEMData;
  /**
   * To store Filtered Lines from getLineBillingDetails and Cart Response
   */
  public filteredLinesModel: LineSelectionModel;
  /**
   * editedLinesCheck to check if we have any edited lines
   */
  public editedLinesCheck: boolean;
  /**
   * boolean flag to notify when required data is loaded
   */
  public dataLoaded: boolean;
  /**
   * Unsubscriber for all subscriptions
   */
  private ngUnsubscribe: Subject<void>;

  /**
   * Stores the static modal for the billing modal data
   */
  public cancelOrderModalData: StaticModal;
  /**
   * Stores the linesInfo information
   */
  public linesInfo: CustomerAccountBillingDetails;
  /**
   * Model to group pending orders by type
   */
  public linePendingOrdersModel: LinePendingOrdersModel;
  /**
   * Variable to hold header text for conflicting pending orders
   */
  public conflictOrderHeader: string;
  /**
   * Boolean flag to check if conflicting pending orders are available
   */
  public pendingOrdersAvailable: boolean;
  /**
   * Flag to show/hide spinner
   */
  public hideSpinner: boolean;
  public ngOnInit(): void {
    this.editedLinesCheck = false;
    this.loadLineSelectorLandingData();
  }

  /**
   * Component life cycle hook to destroy the component
   * @returns void
   */
  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /**
   * function to navigate to set Effective date page
   * @returns void
   */
  public onSetOrderContinue(): void {
    this.router.navigate([CPS_ROUTES.SET_EFFECTIVE_DATE]);
  }

  /**
   * function to navigate to rate plans landing page
   * @returns void
   */
  public onContinueClick(): void {
    this.router.navigate([CPS_ROUTES.CHANGE_PLAN]);
  }

  /**
   * Opens cancel order modal data modal
   *
   * @memberof LineSelectorComponent
   */
  public cancelOrder(): void {
    const checkCartLines = this.objectService.get(this.stateStoreUtilsService.getCart(), 'lines');
    if (checkCartLines) {
      this.cancelOrderModalData.toggleModalEmitter.emit({
        open: true,
        close: false,
      });
    } else {
      this.router.navigate([CPS_ROUTES_CONSTANTS.ACCOUNT_LANDING]);
    }
  }

  /**
   * Method announce the setFocus directive
   * @param event
   */
  public setFocus(): void {
    this.cancelOrderModalData.toggleModalEmitter.emit({
      open: false,
      close: true,
    });
    this.setFocusService.announceFocusUpdate(true);
  }

  /**
   * This method makes a call to the delete Cart API, and updates the cart page
   * @param event
   */
  public handleClickEvent(): void {
    // @TODO: API_2121 for deleting the cart is not ready from ES side so this functionality will implemented in future.
    this.hideSpinner = false;
    this.cartSupportService
      .deleteCart()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        () => {
          this.hideSpinner = true;
          this.router.navigate([CPS_ROUTES_CONSTANTS.ACCOUNT_LANDING]);
        },
        (error: APIError[]) => {
          this.hideSpinner = true;
          this.performCancelOrder();
        }
      );
  }

  /**
   * Method to navigate to order summary page
   * @param orderId: Respective order id whose summary needs to be displayed
   */
  public goToOrderSummary(orderId: string): void {
    this.stateStoreUtilsService.setCustomerOrderId(orderId);
    this.router.navigate([APP_ROUTE_CONSTANTS.ORDER_SUMMARY_URL]);
  }

  /**
   * Loads the customer Data
   * Loads the Line Reltaed Data
   * @returns void
   */
  private loadLineSelectorLandingData(): void {
    this.loadAEMData();
  }

  /**
   * Loads the AEM Data
   * @returns void
   */
  private loadAEMData(): void {
    this.lineSelectorService
      .getLineSelectorContentData()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((aemData: ConfigurableLineSelectorAEMData) => {
        this.logger.debug(
          COMMONLANDING_MODULE_COMPONENTS.COMMONLANDING_MODULE_NAME,
          COMMONLANDING_MODULE_COMPONENTS.LINE_SELECTOR_COMPONENT,
          `recieved content data  in ${COMMONLANDING_MODULE_COMPONENTS.LINE_SELECTOR_COMPONENT},
      data ${JSON.stringify(aemData)}`
        );
        this.aemData = aemData;
        this.buildOrderModalData();
        this.getLineInfo();
      });
  }

  /**
   * Build Cancel Order Modal Data with AEM data passing
   * @returns void
   */
  private buildOrderModalData(): void {
    this.cancelOrderModalData = this.lineSelectorService.cancelOrderModalObject(this.aemData);
  }

  /**
   * Function to get Line information from cache (retreived from 2008 API)
   * create filtered lines model
   * update default line selection in statestore
   */
  private getLineInfo(): void {
    if (this.linesInfo) {
      this.checkEditedOrNonEditedLines();
    } else {
      this.userService
        .getCustomerAccountBillingSummary()
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe((response: CustomerAccountBillingDetails) => {
          this.linesInfo = response;
          this.checkEditedOrNonEditedLines();
        });
    }
  }
  /**
   * Function to set selected lineId
   */
  public onSelect(lineId: string): void {
    this.selectedOption = lineId;
    this.getPendingOrders(lineId);
    this.stateStoreUtilsService.setLineId(this.selectedOption);
    this.stateStoreUtilsService.setMsisdn(this.getMSISDN(lineId));
  }

  /**
   * Method to get the pending orders for the respective line
   * @param lineId - Line Id that was selected
   * @returns void
   */
  public getPendingOrders(lineId: string): void {
    this.hideSpinner = false;
    this.pendingOrdersAvailable = false;
    const msisdn = this.getMSISDN(lineId);
    this.changePlanService
      .getPendingOrder(lineId, msisdn, LINE_LANDING_CONSTANTS.CPS)
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe((orderResults: OrderSearchResults[]) => {
        if (orderResults && orderResults.length) {
          const orderSearchResultsFilterList: OrderSearchResults[] = orderResults.filter(
            (order: OrderSearchResults) => {
              return (
                (order.orderStatus === LINE_LANDING_CONSTANTS.INPROCESS ||
                  order.orderStatus === LINE_LANDING_CONSTANTS.RECEIVED) &&
                order.msisdn === msisdn
              );
            }
          );
          this.linePendingOrdersModel = new LinePendingOrdersModel(
            orderSearchResultsFilterList,
            this.objectService.get(this.aemData, 'orderTypes'),
            true
          );
          this.pendingOrdersAvailable = this.linePendingOrdersModel.ordersByType.length > 0;
          this.conflictOrderHeader = this.commonUtilService.messageFormatter(
            this.objectService.get(this.aemData, 'pendingOrderConflictMessage'),
            [this.phonePipe.transform(msisdn)]
          );
        }
        this.hideSpinner = true;
      });
  }
  /**
   * Method to get the respective MSISDN for a particular line id
   * @param lineId Line Id whose corresponding MSISDN is required
   * @returns string
   */
  public getMSISDN(lineId: string): string {
    const line = this.linesInfo.lines.find(
      (currentLine: LineSummary) => currentLine.lineOfServiceId === lineId
    );
    return line ? line.msisdn : '';
  }
  /**
   * Function to determine which radio button to show selecton on entry
   */
  private radioButtonDefaultSelection(): void {
    const currentLineId = this.stateStoreUtilsService.getLineId();
    if (this.nonEditedLines.length > 0) {
      const selectedLine = this.nonEditedLines.find((line: LineSummary) => {
        return line.lineOfServiceId === currentLineId;
      });
      if (selectedLine) {
        this.selectedOption = selectedLine.lineOfServiceId;
        this.getPendingOrders(this.selectedOption);
      } else {
        this.selectedOption = this.objectService.get(
          this.filteredLinesModel,
          'filteredLine.nonEditedLines.0.lineOfServiceId'
        );
        this.getPendingOrders(this.selectedOption);
        this.stateStoreUtilsService.setLineId(this.selectedOption);
      }
    }
  }
  /**
   * Function to determine which line is edited or not
   */
  private checkEditedOrNonEditedLines(): void {
    const cartResponse = this.stateStoreUtilsService.getCart();
    this.filteredLinesModel = new LineSelectionModel(
      this.linesInfo,
      cartResponse,
      this.objectService
    );
    this.nonEditedLines = this.filteredLinesModel.filteredLine.nonEditedLines.filter(
      (line: LineSummary) => {
        return line.lineStatus !== CPS_CONSTANTS.CANCELLED;
      }
    );
    this.editedLines = this.filteredLinesModel.filteredLine.editedLines;
    this.editedLinesCheck = this.editedLines.length > 0;
    this.radioButtonDefaultSelection();
    this.dataLoaded = true;
  }

  private performCancelOrder(): void {
    this.stateStoreUtilsService.deleteCart();
    this.cartSupportService.announceCartUpdate(null);
    this.router.navigate([CPS_ROUTES_CONSTANTS.ACCOUNT_LANDING]);
  }
}
